import type { QueryClient } from '@tanstack/react-query';

import { _bumpPerf } from '../../devtool/perf-counters.js';
import { fetchWithHttpTelemetry } from '../../transport/http-request.js';
import { hashDiagnosticEntity } from '../../tx-engine/diagnostics/lifecycle.js';
import { emit } from '../events.js';
import { throwHttpError } from '../http-error.js';
import { writeNormalizedEntitySnapshot } from './normalized-entity-write.js';

const DEFAULT_ATTEMPTS = 4;
const DEFAULT_BACKOFF_MS = 250;
const PAGE_SIZE = 100;

export interface TradeEntityReconcileTarget {
  readonly signature: string;
  readonly expectedEventCount: number;
}

export interface TradeEntitiesReconcileResult {
  readonly status: 'updated' | 'pending';
  readonly updatedEventIds: readonly string[];
  readonly pendingSignatures: readonly string[];
  readonly attempts: number;
}

export interface TradeEntityReconcileOptions {
  readonly maxAttempts?: number;
  readonly backoffMs?: number;
  readonly delay?: (milliseconds: number) => Promise<void>;
}

export type TradeEntityReconcileResult = 'updated' | 'pending';

/** Replaces every indexed event for one transaction signature. */
export async function reconcileTradeEntity(
  queryClient: QueryClient,
  signature: string,
  options?: TradeEntityReconcileOptions,
): Promise<TradeEntityReconcileResult> {
  const result = await reconcileTradeEntities(
    queryClient,
    [{ signature, expectedEventCount: 1 }],
    options,
  );
  return result.status;
}

/**
 * Replaces provisional trade bodies without refetching filtered list indexes.
 * Pagination captures every event id for each transaction, while bounded
 * backoff covers the interval between chain confirmation and index visibility.
 */
export async function reconcileTradeEntities(
  queryClient: QueryClient,
  targets: readonly TradeEntityReconcileTarget[],
  options: TradeEntityReconcileOptions = {},
): Promise<TradeEntitiesReconcileResult> {
  const expectedBySignature = normalizeTargets(targets);
  if (expectedBySignature.size === 0) {
    return { status: 'pending', updatedEventIds: [], pendingSignatures: [], attempts: 0 };
  }
  const maxAttempts = positiveInteger(options.maxAttempts, DEFAULT_ATTEMPTS);
  const backoffMs = nonNegativeInteger(options.backoffMs, DEFAULT_BACKOFF_MS);
  const delay = options.delay ?? wait;
  const rowsByEventId = new Map<string, Record<string, unknown>>();
  let pendingSignatures = [...expectedBySignature.keys()];

  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    const observedAtMs = Date.now();
    const rows = await fetchTradeRows(pendingSignatures);
    for (const row of rows) {
      const eventId = nonEmptyString(row.eventId);
      if (eventId === undefined) continue;
      rowsByEventId.set(eventId, row);
      writeNormalizedEntitySnapshot(queryClient, ['trades', 'entity', eventId], row, {
        source: 'rest',
        observedAtMs,
      });
    }

    pendingSignatures = unresolvedSignatures(expectedBySignature, rowsByEventId.values());
    if (pendingSignatures.length === 0) {
      return {
        status: 'updated',
        updatedEventIds: [...rowsByEventId.keys()],
        pendingSignatures: [],
        attempts: attempt,
      };
    }
    if (attempt < maxAttempts) await delay(backoffMs * 2 ** (attempt - 1));
  }

  if (process.env.NODE_ENV !== 'production') _bumpPerf('tradeEntity.reconciliationFailures');
  emit('trade-entity:diagnostic', {
    kind: 'reconciliation-pending',
    entityHash: hashDiagnosticEntity(pendingSignatures.slice().sort().join(',')),
    attempts: maxAttempts,
    pendingSignatureCount: pendingSignatures.length,
  });
  return {
    status: 'pending',
    updatedEventIds: [...rowsByEventId.keys()],
    pendingSignatures,
    attempts: maxAttempts,
  };
}

async function fetchTradeRows(
  signatures: readonly string[],
): Promise<readonly Record<string, unknown>[]> {
  const rows: Record<string, unknown>[] = [];
  const seenCursors = new Set<string>();
  let cursor: string | null = null;
  try {
    do {
      const params = new URLSearchParams({
        signatures: signatures.join(','),
        limit: String(PAGE_SIZE),
      });
      if (cursor !== null) params.set('cursor', cursor);
      const url = `/v1/trades?${params.toString()}`;
      const response = await fetchWithHttpTelemetry({
        operationName: 'reconcileTradeEntities',
        routeTemplate: '/v1/trades',
        requestKind: 'query',
        method: 'GET',
        url,
        init: {
          method: 'GET',
          credentials: 'include',
          headers: { Accept: 'application/json' },
        },
      });
      if (!response.ok) await throwHttpError('GET', url, response);
      const page = readTradePage(await response.json(), signatures);
      rows.push(...page.rows);
      if (page.nextCursor === null || seenCursors.has(page.nextCursor)) break;
      seenCursors.add(page.nextCursor);
      cursor = page.nextCursor;
    } while (cursor !== null);
    return rows;
  } catch (error: unknown) {
    if (process.env.NODE_ENV !== 'production') _bumpPerf('tradeEntity.reconciliationFailures');
    emit('trade-entity:diagnostic', {
      kind: 'reconciliation-failed',
      entityHash: hashDiagnosticEntity(signatures.slice().sort().join(',')),
    });
    throw error;
  }
}

function readTradePage(
  payload: unknown,
  signatures: readonly string[],
): { readonly rows: readonly Record<string, unknown>[]; readonly nextCursor: string | null } {
  if (payload === null || typeof payload !== 'object') return { rows: [], nextCursor: null };
  const record = payload as Record<string, unknown>;
  const signatureSet = new Set(signatures);
  const rows = Array.isArray(record.data)
    ? record.data.filter(
        (row): row is Record<string, unknown> =>
          row !== null &&
          typeof row === 'object' &&
          signatureSet.has(String((row as Record<string, unknown>).signature ?? '')),
      )
    : [];
  return { rows, nextCursor: nonEmptyString(record.nextCursor) ?? null };
}

function normalizeTargets(targets: readonly TradeEntityReconcileTarget[]): Map<string, number> {
  const expected = new Map<string, number>();
  for (const target of targets) {
    const signature = target.signature.trim();
    if (signature.length === 0) continue;
    const count = positiveInteger(target.expectedEventCount, 1);
    expected.set(signature, Math.max(expected.get(signature) ?? 0, count));
  }
  return expected;
}

function unresolvedSignatures(
  expectedBySignature: ReadonlyMap<string, number>,
  rows: Iterable<Record<string, unknown>>,
): string[] {
  const actual = new Map<string, number>();
  for (const row of rows) {
    const signature = nonEmptyString(row.signature);
    if (signature !== undefined) actual.set(signature, (actual.get(signature) ?? 0) + 1);
  }
  return [...expectedBySignature].flatMap(([signature, expected]) =>
    (actual.get(signature) ?? 0) < expected ? [signature] : [],
  );
}

function positiveInteger(value: number | undefined, fallback: number): number {
  return Number.isSafeInteger(value) && (value ?? 0) > 0 ? (value as number) : fallback;
}

function nonNegativeInteger(value: number | undefined, fallback: number): number {
  return Number.isSafeInteger(value) && (value ?? -1) >= 0 ? (value as number) : fallback;
}

function nonEmptyString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function wait(milliseconds: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, milliseconds));
}
